/**
 * Bulk action bar for selected transactions
 */
import { useState } from 'react';
import { useBulkUpdateTransactions } from '@/hooks';
import CategorySelect from './CategorySelect';
import { Alert, Button } from '@/components/ui';
import { ApiClientError } from '@/services/api';
import { BulkUpdatePartialError } from '@/services/similarTransactions';
import { getUserFriendlyMessage } from '@/utils/errorMessages';
import { cn } from '@/utils/cn';

/**
 * What to tell the user when a bulk update fails. A partial failure (some
 * chunks saved, a later one failed) says how many were and were not updated,
 * so the bar never implies the whole selection was categorized (WP-BUG-65).
 */
function getBulkErrorMessage(error: unknown): string {
  if (error instanceof BulkUpdatePartialError) {
    return error.message;
  }
  if (error instanceof ApiClientError) {
    return getUserFriendlyMessage(error.category, error.requestId);
  }
  return 'The category could not be assigned. Please try again.';
}

export interface BulkActionsProps {
  selectedCount: number;
  selectedIds: string[];
  onClearSelection: () => void;
  onSuccess?: () => void;
  className?: string;
}

/**
 * Bulk action bar that appears when transactions are selected
 */
export default function BulkActions({
  selectedCount,
  selectedIds,
  onClearSelection,
  onSuccess,
  className,
}: BulkActionsProps) {
  const [showCategorySelect, setShowCategorySelect] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const { mutate: bulkUpdate, isPending } = useBulkUpdateTransactions();

  const handleCategoryChange = (categoryId: string | undefined) => {
    if (!categoryId) return;

    setErrorMessage(null);
    bulkUpdate(
      {
        transactionIds: selectedIds,
        payload: { categoryId },
      },
      {
        onSuccess: () => {
          setShowCategorySelect(false);
          onClearSelection();
          if (onSuccess) {
            onSuccess();
          }
        },
        // Keep the selection so the user can retry; re-applying is idempotent.
        onError: (error) => {
          setErrorMessage(getBulkErrorMessage(error));
        },
      }
    );
  };

  if (selectedCount === 0) {
    return null;
  }

  return (
    <div
      className={cn(
        'fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-30',
        'transform transition-transform duration-300',
        className
      )}
    >
      <div className="max-w-7xl mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <span className="text-sm font-medium text-gray-700">
              {selectedCount} transaction{selectedCount !== 1 ? 's' : ''} selected
            </span>
            <button
              type="button"
              onClick={onClearSelection}
              className="ml-4 text-sm text-primary-600 hover:text-primary-700 font-medium"
            >
              Clear selection
            </button>
          </div>

          <div className="flex items-center space-x-3">
            {/* Category assignment */}
            {showCategorySelect ? (
              <div className="flex items-center space-x-2">
                {/*
                  Bulk edit: Intentionally NOT filtering by categoryType.
                  When multiple transactions are selected, they may have mixed
                  amount signs (some income, some expense), so we show all categories.
                */}
                <CategorySelect
                  value={undefined}
                  onChange={handleCategoryChange}
                  placeholder="Select category"
                  allowClear={false}
                  disabled={isPending}
                  className="w-48"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowCategorySelect(false)}
                  disabled={isPending}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowCategorySelect(true)}
                leftIcon={
                  <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z"
                      clipRule="evenodd"
                    />
                  </svg>
                }
              >
                Assign Category
              </Button>
            )}
          </div>
        </div>

        {errorMessage && (
          <Alert variant="error" className="mt-2">
            {errorMessage}
          </Alert>
        )}

        {/* Loading indicator */}
        {isPending && (
          <div className="mt-2">
            <div className="h-1 bg-gray-200 rounded overflow-hidden">
              <div className="h-full bg-primary-500 animate-pulse" style={{ width: '100%' }} />
            </div>
            <p className="text-xs text-gray-500 mt-1">Updating transactions...</p>
          </div>
        )}
      </div>
    </div>
  );
}
